<template>
  <BaseModal
    :heading="`${$t(`reports.totals.${variables.type.toLowerCase()}`, brandNamePay())} ${$t('reports.ranges.transactions').toLowerCase()}`"
    small
    @close="$emit('close')"
  >
    <div :class="$style.header">
      <DateSelect
        v-bind="{ startDate, endDate }"
        :label="$t('global.actions.period')"
        @select="onDateSelect"
      />
      <BaseDropdown
        v-model="variables.type"
        :options="
          types.map((type) => ({
            value: type,
            label: $t(`reports.totals.${type.toLowerCase()}`, brandNamePay())
          }))
        "
        :label="$t('reports.totals.payment_method')"
        v-test="'payment-method-select'"
      />
      <div
        v-if="variables.type === 'POS' && unleash.isEnabled('SalonizedPayTerminalTipping')"
        :class="$style.buttonWrapper"
      >
        <BaseButton
          @click="showTips"
          v-test="'show-tips-button'"
        >
          {{ $t('reports.totals.show_tips') }}
        </BaseButton>
      </div>
    </div>
    <div v-if="firstFetchFinished && transactions.length === 0 && !loading">
      {{ $t('global.no_results') }}
    </div>
    <BaseTable
      v-else-if="firstFetchFinished && transactions.length"
      :headers="
        [
          $t('global.date'),
          $t('global.amount'),
          variables.type === 'POS' ? $t('reports.totals.transaction_costs') : ''
        ].filter((header) => !!header)
      "
      :rows="
        transactions.map((transaction) => ({
          id: transaction.orderId,
          cells: [
            filters.dateTime(transaction.transactionAt),
            filters.currency(transaction.amount),
            variables.type === 'POS'
              ? transaction.posPayment
                ? filters.currency(transaction.posPayment.transactionFee)
                : false
              : undefined
          ].filter((cell) => cell !== undefined),
          actions: [orderPdfIcon()]
        }))
      "
      @action="showPdf"
      v-test="'transactions-table'"
    />
    <LoadMore
      v-show="firstFetchFinished && !allDataFetched"
      @shown="fetchMore"
    />
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import gql from 'graphql-tag';
import { usePagination } from '@/apollo/pagination';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import { orderPdfIcon, orderPdfRoute } from '@/helpers/order-pdf-route';
import { useTreatwellStore } from '@/stores/treatwell';
import unleash from '@/unleash';
import DateSelect from '../components/DateSelect.vue';
import type { PaymentMethod } from './types';

const props = defineProps<{
  selectedMethod: PaymentMethod;
  startDate: string;
  endDate: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'show-tips', startDate: string, endDate: string): void;
}>();

const {
  transactions,
  firstFetchFinished,
  allDataFetched,
  variables,
  fetchMore,
  loading,
} = usePagination({
  fieldName: 'transactions',
  query: gql`
    query getTransactions(
      $type: TransactionType
      $endDate: DateTime
      $pagination: PaginationAttributes
      $startDate: DateTime
      $onlyWithOrderId: Boolean
      $direction: SortDirectionEnum
      $sortBy: String
    ) {
      transactions(
        type: $type
        endDate: $endDate
        pagination: $pagination
        startDate: $startDate
        onlyWithOrderId: $onlyWithOrderId
        sortBy: $sortBy
        direction: $direction
      ) {
        amount
        id
        orderId
        posPayment {
          transactionFee
        }
        transactionAt
      }
    }
  `,
  variables: {
    direction: 'DESC',
    endDate: props.endDate,
    onlyWithOrderId: true,
    sortBy: 'transaction_at',
    startDate: props.startDate,
    type: props.selectedMethod.key.toUpperCase(),
  },
});

const types = ref([
  'BANK',
  'CASH',
  'COUPON',
  'CREDITCARD',
  'GIFTCARD',
  'ONLINE',
  'PIN',
  'PREPAID_CARD',
]);

const hasSalonizedPay = inject<any>('hasSalonizedPay');
if (hasSalonizedPay.value) {
  types.value.push('POS');
}

const { hasActiveVenue } = useTreatwellStore();
if (hasActiveVenue) {
  types.value.push('TREATWELL_ONLINE_PAYMENT');
}

const showPdf = ({ id }: { id: string }) => {
  orderPdfRoute(id);
};

const showTips = () => {
  emit('show-tips', variables.startDate, variables.endDate);
};

const onDateSelect = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  variables.startDate = startDate;
  variables.endDate = endDate;
};
</script>

<style
  lang="scss"
  module
>
.header {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  margin-bottom: $spacing;
}

.buttonWrapper {
  flex-shrink: 0;
  align-self: end;
  width: fit-content;
}
</style>
